import { alpha } from "@mui/material/styles";
import { Box, Chip, Stack, Typography } from "../ds";
import type { ChipColorVariant } from "../ds";
import AttachmentLink from "./AttachmentLink";
import { reviewerInitials } from "../utils/reviewers";

// The label step shared by every read-only record on the detail page.
export const RECORD_LABEL_SX = {
  fontSize: 12,
  fontWeight: 600,
  color: "text.secondary",
  letterSpacing: 0.2,
} as const;

/**
 * Column slots of the record grid: what it is about on the left, who
 * recorded it (and when) on the right. Slots are honoured only when a row
 * fills all three; a sparser row packs its fields from the left in slot
 * order, so a lone "Verified by" never sits behind two empty columns.
 */
export const RECORD_COLUMN = { subject: 1, detail: 2, recordedBy: 3 } as const;

export interface RecordDetail {
  label: string;
  value: React.ReactNode;
  /** Column slot on sm+ (see RECORD_COLUMN); also sets packing order. */
  column?: 1 | 2 | 3;
  /** Take a whole row of its own (lists: verifier departments, …). */
  span?: boolean;
}

/**
 * Labelled key/value fields of a recorded decision on one three-column
 * grid (one column on xs). Every read-only record on the detail page —
 * both evaluations, each department's implementation, the custodian's
 * closure — lays its fields out through this, so they line up with each
 * other. Callers pass only the fields that hold a value.
 */
export function RecordDetails({ items }: { items: RecordDetail[] }) {
  if (items.length === 0) return null;
  const cells = items.filter((i) => !i.span);
  const useSlots = new Set(cells.map((i) => i.column)).size === 3 &&
    cells.every((i) => i.column);
  const ordered = [
    ...[...cells].sort((a, b) => (a.column ?? 4) - (b.column ?? 4)),
    ...items.filter((i) => i.span),
  ];
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(3, minmax(0, 1fr))",
        },
        columnGap: 2,
        rowGap: 2,
      }}
    >
      {ordered.map(({ label, value, column, span }) => (
        <Box
          key={label}
          sx={{
            minWidth: 0,
            gridColumn: {
              xs: "auto",
              sm: span
                ? "1 / -1"
                : useSlots && column
                  ? String(column)
                  : "auto",
            },
          }}
        >
          <Typography component="dt" sx={RECORD_LABEL_SX}>
            {label}
          </Typography>
          <Box component="dd" sx={{ m: 0, fontSize: 14, minWidth: 0 }}>
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** A run of labels as chips — one chip style for every record list. */
export function RecordChips({
  labels,
  tone = "info",
}: {
  labels: readonly string[];
  tone?: ChipColorVariant;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      flexWrap="wrap"
      useFlexGap
      sx={{ mt: 0.25 }}
    >
      {labels.map((label, i) => (
        <Chip
          key={`${label}-${i}`}
          label={label}
          chipVariant="outlined"
          colorVariant={tone}
        />
      ))}
    </Stack>
  );
}

/** A quiet explanatory line under a sheet (how a score was derived, …). */
export function RecordCaption({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

/** The vertical rhythm every record's parts sit in. */
export function RecordBlock({ children }: { children: React.ReactNode }) {
  return <Stack spacing={1.75}>{children}</Stack>;
}

/** Who recorded it, with the timestamp as a caption under the name. */
export function RecordedBy({ name, at }: { name: string; at?: string }) {
  return (
    <>
      {name}
      {at && (
        <Typography
          component="span"
          sx={{ display: "block", fontSize: 12, color: "text.secondary" }}
        >
          {at}
        </Typography>
      )}
    </>
  );
}

/**
 * Free text someone typed, set as plain body text under its label — the
 * same type as the comment thread, no inset box or accent stripe. Line
 * breaks are kept, and long unbroken tokens (pasted ids, URLs, keyboard
 * mash) wrap instead of overflowing the card.
 */
export function RecordTextPanel({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      {label && <Typography sx={RECORD_LABEL_SX}>{label}</Typography>}
      <Typography
        component="div"
        sx={{
          mt: label ? 0.5 : 0,
          fontSize: 14,
          lineHeight: 1.55,
          color: "text.primary",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export interface RecordThreadEntry {
  author: string;
  at?: string;
  text: string;
}

/**
 * A labelled run of comments as a plain activity list: the author's
 * initials (the rail's reviewer avatar), name and time on one line, the
 * text under the name. No bubble, tint or accent stripe — whitespace
 * separates the entries. Renders nothing for an empty thread.
 */
export function RecordThread({
  label = "Comments",
  entries,
  showCount = true,
}: {
  /** `null` drops the label, for a thread whose toggle already names it. */
  label?: string | null;
  entries: readonly RecordThreadEntry[];
  /** Off for a record's single comment ("Comment", not "Comment (1)"). */
  showCount?: boolean;
}) {
  if (entries.length === 0) return null;
  return (
    <Box>
      {label !== null && (
        <Typography sx={RECORD_LABEL_SX}>
          {showCount ? `${label} (${entries.length})` : label}
        </Typography>
      )}
      <Stack
        component="ul"
        spacing={2}
        sx={{ m: 0, mt: label !== null ? 1.25 : 0, p: 0, listStyle: "none" }}
      >
        {entries.map((e, idx) => (
          <Stack
            component="li"
            key={`${e.at ?? ""}-${idx}`}
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
          >
            <Box
              aria-hidden="true"
              sx={(theme) => ({
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.dark",
                display: "grid",
                placeItems: "center",
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
              })}
            >
              {reviewerInitials(e.author)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                alignItems="baseline"
                spacing={0.75}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography
                  sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: "28px" }}
                >
                  {e.author}
                </Typography>
                {e.at && (
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {e.at}
                  </Typography>
                )}
              </Stack>
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "text.primary",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {e.text}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** A labelled row of attachments; renders nothing when there are none. */
export function RecordAttachments({
  ids,
  label = "Attachments",
}: {
  ids: readonly string[] | null | undefined;
  label?: string;
}) {
  if (!ids || ids.length === 0) return null;
  return (
    <Box>
      <Typography sx={RECORD_LABEL_SX}>{label}</Typography>
      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="wrap"
        useFlexGap
        alignItems="flex-end"
        sx={{ mt: 0.75 }}
      >
        {ids.map((aid) => (
          <AttachmentLink key={aid} assetId={aid} />
        ))}
      </Stack>
    </Box>
  );
}
